/**
 * Shared sound design helpers — Web Audio synthesis (no asset files, no
 * autoplay headaches once the user has tapped anywhere). Every helper
 * honors the global `ecDashCueMuted` flag the user toggles via the
 * speaker icon in the home header, so silencing one place silences all.
 *
 * Why synthesis instead of mp3 files: we can't ship audio assets reliably
 * to iOS PWAs (Safari pre-caches inconsistently), and small WAV/MP3 files
 * still add ~50KB each. Three short oscillator beeps weigh 0 bytes.
 */

let _ctx = null;
function getCtx() {
  if (_ctx) return _ctx;
  try {
    const Ctx = typeof window !== "undefined" ? (window.AudioContext || window.webkitAudioContext) : null;
    if (Ctx) _ctx = new Ctx();
  } catch (e) { /* no audio support */ }
  return _ctx;
}

function isMuted() {
  try { return localStorage.getItem("ecDashCueMuted") === "1"; } catch { return false; }
}

function tone(freq, start, dur, gain = 0.12, type = "sine") {
  const ctx = getCtx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
    g.gain.exponentialRampToValueAtTime(gain, ctx.currentTime + start + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start(ctx.currentTime + start);
    osc.stop(ctx.currentTime + start + dur + 0.05);
  } catch (e) { /* ignore — autoplay block before first gesture */ }
}

// Subtle "pop" — used for tile taps. Single ~60ms square wave at A5.
export function playTap() {
  if (isMuted()) return;
  tone(880, 0, 0.07, 0.06, "triangle");
}

// Victory chime — 3-note ascending arpeggio (C-E-G), for milestone hits.
// Louder + longer than the unread cue so it reads as a celebration.
export function playVictory() {
  if (isMuted()) return;
  tone(523.25, 0, 0.22, 0.18);                // C5
  tone(659.25, 0.12, 0.22, 0.18);             // E5
  tone(783.99, 0.24, 0.34, 0.22);             // G5 (held longer)
  // Final sparkle: high C7 ping
  tone(2093.0, 0.34, 0.28, 0.16, "triangle");
}

// Walkie chime — quick "beep-boop" so a live listener knows a clip
// just arrived. Two short descending tones = normal clip. Ascending +
// louder + repeated = emergency. Piggybacks on the same isMuted() gate
// used by playTap so users who muted the app stay muted.
export function playWalkieChime() {
  if (isMuted()) return;
  tone(880, 0, 0.09, 0.16, "triangle"); // A5
  tone(659.25, 0.09, 0.12, 0.14, "triangle"); // E5
}

// Louder, more insistent 3-beep pattern for Emergency broadcasts.
// Higher, longer, at ~2× the gain of the normal chime so it cuts
// through even in a noisy store.
export function playWalkieEmergencyChime() {
  if (isMuted()) return;
  tone(783.99, 0.00, 0.16, 0.32, "square"); // G5
  tone(1046.5, 0.18, 0.16, 0.32, "square"); // C6
  tone(1318.5, 0.36, 0.28, 0.36, "square"); // E6 (held longer)
  // Paired triple-buzz haptic so pockets and jackets can't muffle it.
  // Chrome/Android + Chromium-based iOS PWA support `navigator.vibrate`;
  // Safari ignores this call silently. Pattern is [buzz, gap, buzz,
  // gap, buzz] all in ms — matches the 3-beep audio cadence so the
  // phone shakes on the same beats as the chime.
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate([400, 120, 400, 120, 400]);
    }
  } catch { /* ignore */ }
}

// Sender-side "key up" chirp — a short high blip the moment the user
// presses the mic. Classic walkie feel: tells them the channel is
// open and they can start talking. Kept very short (~60ms) so it
// doesn't clip the leading edge of what they say.
export function playWalkieStartChirp() {
  if (isMuted()) return;
  tone(1568.0, 0, 0.05, 0.22, "square"); // G6
}

// Sender-side "key down" chirp — descending two-tone the moment they
// release the mic. Confirms the transmission was captured; contrasts
// with the receive chime so the sender can tell "I sent" from
// "someone sent to me".
export function playWalkieEndChirp() {
  if (isMuted()) return;
  tone(1046.5, 0.00, 0.06, 0.20, "square"); // C6
  tone(659.25, 0.06, 0.09, 0.18, "square"); // E5
}


// Lightweight tap haptic for mobile — pairs with playTap on tile clicks.
export function tapHaptic() {
  if (isMuted()) return;
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(8);
  } catch (e) { /* ignore */ }
}
